"use client";

import React, { useMemo } from "react";
import { Phone, AlertCircle } from "lucide-react";
import { useRouter } from "next/navigation";
import { useLanguage } from "@/contexts/LanguageContext";
import { getStrings, interpolate } from "@/i18n/dictionaries";

/**
 * Generic component to display when user tries a Flutterwave payment method
 * whose phone calling-code or profile country doesn't match the selected
 * payment country.  Shows a compact summary and alternatives.
 *
 * Used by all country-specific prompt wrappers (Nigeria, Ghana, Kenya, etc.).
 */
export default function CountryPhonePrompt({
  onClose,
  onSwitchToStripe,
  onSwitchToBankTransfer,
  detectedPhone,          // Final resolved phone (for diagnostics)
  authPhone,              // Phone from Firebase Auth (unused in display now)
  profilePhone,           // Phone from Firestore profile
  profileCountry,         // User's profile country (from Firestore currentCountry)
  selectedPaymentCountry, // Country user selected in the payment UI
  config,                 // Configuration object for the specific payment method
}) {
  const router = useRouter();
  const { locale } = useLanguage();
  const t = useMemo(() => getStrings(locale, "payment"), [locale]);

  const {
    title = t.phone_mismatch,
    requiredPrefix = "+234",
    requiredCountryName = "",
  } = config || {};

  // Derive a short calling-code diagnostic string
  const phoneCCDisplay = detectedPhone
    ? (detectedPhone.startsWith(requiredPrefix)
        ? `${requiredPrefix} ✓`
        : `${detectedPhone.slice(0, 4)} (expected ${requiredPrefix})`)
    : "—";

  const handleUpdatePhone = () => {
    onClose();
    router.push("/onboarding?tab=profile");
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-3">
      <div className="relative w-full max-w-xl max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-xl bg-white shadow-2xl">
        {/* Header */}
        <div className="border-b border-slate-200 px-5 py-3 flex flex-col items-center gap-2">
          <div className="rounded-full bg-blue-50 p-2">
            <Phone className="h-5 w-5 text-blue-600" />
          </div>
          <h2 className="text-lg font-bold text-slate-900 leading-snug text-center">
            {title}
          </h2>
        </div>

        {/* Content */}
        <div className="px-5 py-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-600" />
              <div className="text-sm leading-relaxed text-blue-900 space-y-2">
                {/* Requirements */}
                <p className="font-semibold">{interpolate(t.flw_requires, { country: requiredCountryName || selectedPaymentCountry })}</p>
                <ul className="list-disc list-inside ml-1 space-y-0.5">
                  <li>{t.phone_number_label} <span className="font-medium">{requiredPrefix}</span></li>
                  <li>{t.profile_country_label} <span className="font-medium">{requiredCountryName || selectedPaymentCountry || "—"}</span></li>
                </ul>

                {/* User details */}
                <p className="font-semibold">{t.your_details}</p>
                <ul className="list-disc list-inside ml-1 space-y-0.5 text-blue-700">
                  <li>{t.phone_label} <span className="font-medium">{profilePhone || t.not_set}</span></li>
                  <li>{t.profile_country_label} <span className="font-medium">{profileCountry || t.not_set}</span></li>
                  <li>{t.selected_payment_country} <span className="font-medium">{selectedPaymentCountry || "—"}</span></li>
                  <li>{t.phone_calling_code} <span className="font-medium">{phoneCCDisplay}</span></li>
                </ul>

                <p className="text-blue-800">
                  {t.update_phone_desc}
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="mt-3 space-y-2">
            <button
              onClick={handleUpdatePhone}
              className="w-full rounded-md bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-500 transition"
            >
              <Phone className="inline h-4 w-4 mr-2" />
              {t.update_phone_btn}
            </button>

            {onSwitchToStripe && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-slate-300"></div>
                  </div>
                  <div className="relative flex justify-center text-xs">
                    <span className="bg-white px-2 text-slate-500 font-medium">{t.or_alt_payment}</span>
                  </div>
                </div>

                <button
                  onClick={() => { onClose(); onSwitchToStripe(); }}
                  className="w-full rounded-md bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 transition"
                >
                  {t.pay_with_card}
                </button>
                <p className="text-xs text-slate-600 text-center">{t.works_any_phone}</p>
              </>
            )}

            {onSwitchToBankTransfer && (
              <button
                onClick={() => { onClose(); onSwitchToBankTransfer(); }}
                className="w-full rounded-md bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-emerald-500 transition"
              >
                {t.use_bank_transfer}
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 px-5 py-3">
          <button
            onClick={onClose}
            className="w-full rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition"
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
}
